// src/components/RolesEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";

// --- Define your permission matrix here (easy to extend later)
// ---- Permissions matrix used by the Roles editor columns ----
const SCOPES = Object.freeze({
  dashboard: ["view"],        // KPI visibility toggle
  sales: ["read", "create", "update", "delete"],
  stock: ["read", "create", "update", "delete"],
  reports: ["view", "export"]
});


// tiny helper
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || `role_${Date.now()}`;

export default function RolesEditor() {
  const nav = useNavigate();

  const [me, setMe] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // roles list
  const [roles, setRoles] = useState([]);
  const [savingId, setSavingId] = useState(null);

  // --- Auth + admin check (independent of DashboardV2)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setMe(u || null);
      if (!u) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "allowedUsers", u.uid));
        setIsAdmin(!!snap.exists() && (snap.data().role === "admin"));
      } catch {
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // --- Load roles live
  useEffect(() => {
  // If not admin, clear the table and do not subscribe
  if (!isAdmin) { setRoles([]); return; }

  const unsub = onSnapshot(collection(db, "roles"), (snap) => {
    const rows = snap.docs.map((d) => ({
      id: d.id,
      name: d.data().name || d.id,
      description: d.data().description || "",
      perms: d.data().perms || {},
      _dirty: false,
      _new: false,
    }));

    // De-dupe by id (prevents “auto clone” if a temp row remains locally)
    const byId = new Map(rows.map((r) => [r.id, r]));
    const unique = Array.from(byId.values());

    // (optional) keep nice order
    unique.sort((a, b) => a.name.localeCompare(b.name));

    setRoles(unique);
  });

  return () => unsub();
}, [isAdmin]);


  const headers = useMemo(
    () => Object.entries(SCOPES), // [ [scope, [actions...]], ... ]
    []
  );

  const togglePerm = (rid, scope, action) => {
    setRoles((arr) =>
      arr.map((r) => {
        if (r.id !== rid) return r;
        const next = structuredClone(r);
        if (!next.perms) next.perms = {};
        if (!next.perms[scope]) next.perms[scope] = {};
        next.perms[scope][action] = !next.perms[scope][action];
        next._dirty = true;
        return next;
      })
    );
  };

  const setMeta = (rid, field, value) => {
    setRoles((arr) =>
      arr.map((r) => (r.id === rid ? { ...r, [field]: value, _dirty: true } : r))
    );
  };

  const addRole = () => {
    const newId = `role_${Date.now()}`;
    setRoles((arr) => [
      ...arr,
      {
        id: newId,
        name: "",
        description: "",
        perms: {},
        _dirty: true,
        _new: true,
      },
    ]);
  };

  const cloneRole = (r) => {
    const copyId = `${r.id}_copy_${Date.now()}`;
    setRoles((arr) => [
      ...arr,
      {
        id: copyId,
        name: `${r.name} (copy)`,
        description: r.description,
        perms: structuredClone(r.perms || {}),
        _dirty: true,
        _new: true,
      },
    ]);
  };

  const deleteRole = async (r) => {
    if (!window.confirm(`Delete role "${r.name || r.id}"?`)) return;
    try {
      if (!r._new) await deleteDoc(doc(db, "roles", r.id));
      setRoles((arr) => arr.filter((x) => x.id !== r.id));
    } catch (e) {
      alert(`Delete failed: ${e.message || e}`);
    }
  };

  const saveRole = async (r) => {
  const id = r._new ? slugify(r.name) : r.id;   // new → slug id, existing → keep id
  if (!id) return alert("Role name is required.");
  setSavingId(r.id);
  try {
    const data = {
      name: r.name || id,
      description: r.description || "",
      perms: r.perms || {},
    };

    await setDoc(doc(db, "roles", id), data, { merge: true });

    // ✅ Let the snapshot be the source of truth
    // If this was a NEW temp row, remove it; snapshot will add the saved doc.
    // If it was an existing row, just clear the dirty flag.
    setRoles((arr) =>
      r._new
        ? arr.filter((x) => x.id !== r.id) // drop temp row
        : arr.map((x) => (x.id === id ? { ...x, _dirty: false } : x))
    );
  } catch (e) {
    console.error(e);
    alert("Save failed");
  } finally {
    setSavingId(null);
  }
};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading…</div>
      </div>
    );
  }
  if (!me || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="p-6 bg-white border rounded shadow text-center">
          <div className="text-lg font-semibold mb-2">Not authorized</div>
          <p className="text-sm text-gray-600">
            You need admin access to manage roles.
          </p>
          <button
            className="mt-3 px-3 py-1.5 rounded bg-slate-700 text-white"
            onClick={() => nav("/dashboard")}
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f7fb] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-[#094a85]">Roles Manager</h1>
          <div className="flex items-center gap-2">
            
            <button
              onClick={addRole}
              className="px-3 py-1.5 rounded bg-blue-600 text-white"
            >
              New Role
            </button>
          </div>
        </div>

        <div className="overflow-x-auto bg-white border rounded">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3 text-left w-[22rem]">Role</th>
                <th className="p-3 text-left">Description</th>
                {/* dynamic headers */}
                {headers.map(([scope, actions]) => (
                  <th key={scope} className="p-3 text-center">
                    <div className="font-medium capitalize">{scope}</div>
                    <div className="text-[11px] text-gray-500">
                      {actions.join(" · ")}
                    </div>
                  </th>
                ))}
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id} className="border-t align-top">
                  <td className="p-3">
                    <input
                      className="w-full border rounded px-2 py-1"
                      value={r.name}
                      onChange={(e) => setMeta(r.id, "name", e.target.value)}
                      placeholder="Role name (e.g., Inventory Manager)"
                    />
                    <div className="text-[11px] text-gray-500 mt-1">
                      <b>ID:</b> {r._new ? <em>(new)</em> : r.id}
                    </div>
                  </td>
                  <td className="p-3">
                    <textarea
                      className="w-full border rounded px-2 py-1 min-h-[40px]"
                      value={r.description}
                      onChange={(e) =>
                        setMeta(r.id, "description", e.target.value)
                      }
                      placeholder="Short description"
                    />
                  </td>

                  {headers.map(([scope, actions]) => (
                    <td key={`${r.id}_${scope}`} className="p-3">
                      <div className="flex flex-wrap gap-2">
                        {actions.map((action) => {
                          const checked = !!r.perms?.[scope]?.[action];
                          return (
                            <label
                              key={`${r.id}_${scope}_${action}`}
                              className="inline-flex items-center gap-1 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  togglePerm(r.id, scope, action)
                                }
                              />
                              <span className="capitalize">{action}</span>
                            </label>
                          );
                        })}
                      </div>
                    </td>
                  ))}

                  <td className="p-3 space-x-2 whitespace-nowrap">
                    <button
                      disabled={!r._dirty || savingId === r.id}
                      onClick={() => saveRole(r)}
                      className={`px-3 py-1 rounded text-white ${
                        r._dirty
                          ? "bg-blue-600 hover:bg-blue-700"
                          : "bg-gray-300 cursor-not-allowed"
                      }`}
                    >
                      {savingId === r.id ? "Saving…" : "Save"}
                    </button>
                    <button
                      onClick={() => cloneRole(r)}
                      className="px-3 py-1 rounded border"
                    >
                      Clone
                    </button>
                    <button
                      onClick={() => deleteRole(r)}
                      className="px-3 py-1 rounded border text-red-600"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {roles.length === 0 && (
                <tr>
                  <td className="p-6 text-center text-gray-500" colSpan={5 + headers.length}>
                    No roles yet. Click <b>New Role</b> to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-xs text-gray-500">
          Tips: Admin role bypasses all checks. IDs are used in rules; renaming a role keeps permissions but the ID of a
          newly created role is a slug of the name.
        </p>
      </div>
    </div>
  );
}
